import { GUIA_LABELS } from '@utils/guiaLabels.js'
import { memo, useCallback, useState } from 'react'
import { Button } from '@components/primitives/Button.js'
import { Input } from '@components/primitives/Input.js'
import { Select } from '@components/primitives/Select.js'
import { PassoObraApplySection } from './PassoObraApplySection.js'
import type { MetadadoField } from '@content/topicos-content.js'
import { formatDatetime } from '@utils/format.js'

const MetadadoInput = memo(function MetadadoInput({
  field,
  value,
  onChange,
}: {
  field: MetadadoField
  value: string
  onChange: (v: string) => void
}) {
  if (field.tipo === 'select' && field.opcoes) {
    return (
      <Select
        label={field.label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        options={[
          { value: '', label: 'Selecione…' },
          ...field.opcoes.map((o) => ({ value: o, label: o })),
        ]}
      />
    )
  }
  if (field.tipo === 'textarea') {
    return (
      <div className='cf-field'>
        <label className='cf-field__label'>{field.label}</label>
        <textarea
          className='cf-field__input'
          value={value}
          placeholder={field.placeholder ?? ''}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
        />
      </div>
    )
  }
  return (
    <Input
      label={field.label}
      type={field.tipo === 'number' ? 'number' : 'text'}
      value={value}
      placeholder={field.placeholder ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  )
})

interface DiarioEntry {
  id?: number
  texto: string
  criadoEm: string
}

interface Props {
  metadados: MetadadoField[]
  getMetaValue: (campo: string) => string
  onSaveMeta: (campo: string, v: string) => void
  diario: DiarioEntry[]
  novaEntrada: string
  onNovaEntradaChange: (v: string) => void
  onSalvarEntrada: () => void
  salvando: boolean
  onEditEntrada: (id: number, texto: string) => void
  onDeleteEntrada: (id: number) => void
  nested?: boolean
  passoTitulo?: string
  moduloId?: string
  topicoId?: string
  temFerramenta?: boolean
  checklistItems?: { id: string; texto: string; concluido: boolean }[]
  percentualPratico?: number
  onToggleChecklist?: (id: string) => void
  onMarcarTodos?: (marcar: boolean) => void
}

export function PassoRegistrarSection({
  metadados,
  getMetaValue,
  onSaveMeta,
  diario,
  novaEntrada,
  onNovaEntradaChange,
  onSalvarEntrada,
  salvando,
  onEditEntrada,
  onDeleteEntrada,
  nested,
  topicoId = '',
  temFerramenta = false,
  checklistItems = [],
  percentualPratico = 0,
  onToggleChecklist,
  onMarcarTodos,
}: Props) {
  const [editId, setEditId] = useState<number | null>(null)
  const [editText, setEditText] = useState('')
  const [deleteId, setDeleteId] = useState<number | null>(null)

  const startEdit = useCallback((entry: DiarioEntry) => {
    if (entry.id == null) return
    setEditId(entry.id)
    setEditText(entry.texto)
  }, [])

  return (
    <section
      className={nested ? 'cf3-block cf3-block--nested' : 'cf3-block'}
      aria-labelledby='registrar-heading'
    >
      <h2 id='registrar-heading' className='cf3-subsection-title'>
        {nested ? 'Registrar na obra' : 'Registrar'}
      </h2>

      {(temFerramenta || checklistItems.length > 0) &&
        topicoId &&
        onToggleChecklist &&
        onMarcarTodos && (
          <PassoObraApplySection
            topicoId={topicoId}
            temFerramenta={temFerramenta}
            checklistItems={checklistItems}
            percentualPratico={percentualPratico}
            onToggleChecklist={onToggleChecklist}
            onMarcarTodos={onMarcarTodos}
          />
        )}

      {metadados.length > 0 && (
        <div className='cf3-block__card'>
          <p className='cf3-overline'>{GUIA_LABELS.dadosObraNestaEtapa}</p>
          <div className='cf3-meta-form'>
            {metadados.map((field) => (
              <MetadadoInput
                key={field.campo}
                field={field}
                value={getMetaValue(field.campo)}
                onChange={(v) => onSaveMeta(field.campo, v)}
              />
            ))}
          </div>
        </div>
      )}

      <div className='cf3-block__card'>
        <p className='cf3-overline'>{GUIA_LABELS.diarioDaEtapa}</p>
        <div className='cf3-diario-pass'>
          <textarea
            className='cf3-diario-pass__input'
            placeholder='Observações, decisões e anotações…'
            value={novaEntrada}
            onChange={(e) => onNovaEntradaChange(e.target.value)}
            rows={3}
            aria-label='Nova entrada no diário'
          />
          <Button
            variant='primary'
            className='cf3-diario-pass__save'
            onClick={onSalvarEntrada}
            loading={salvando}
            disabled={!novaEntrada.trim()}
          >
            Salvar nota
          </Button>
        </div>
        <div className='cf3-diario-pass__entries'>
          {diario.map((entry) => (
            <div key={entry.id} className='cf3-diario-pass-entry'>
              {editId === entry.id ? (
                <>
                  <textarea
                    className='cf3-diario-pass__input'
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={2}
                  />
                  <Button
                    size='sm'
                    onClick={() => {
                      if (entry.id != null) onEditEntrada(entry.id, editText)
                      setEditId(null)
                    }}
                  >
                    Salvar
                  </Button>
                </>
              ) : (
                <>
                  <div className='cf3-diario-pass-entry__top'>
                    <span className='cf3-diario-pass-entry__data'>
                      {formatDatetime(entry.criadoEm)}
                    </span>
                    {entry.id != null && (
                      <div className='cf3-diario-pass-entry__actions'>
                        {!deleteId || deleteId !== entry.id ? (
                          <>
                            <button
                              type='button'
                              className='cf3-diario-pass-entry__btn'
                              onClick={() => startEdit(entry)}
                            >
                              Editar
                            </button>
                            <button
                              type='button'
                              className='cf3-diario-pass-entry__btn cf3-diario-pass-entry__btn--danger'
                              onClick={() => setDeleteId(entry.id!)}
                            >
                              Excluir
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type='button'
                              className='cf3-diario-pass-entry__btn cf3-diario-pass-entry__btn--danger'
                              onClick={() => {
                                onDeleteEntrada(entry.id!)
                                setDeleteId(null)
                              }}
                            >
                              Confirmar
                            </button>
                            <button
                              type='button'
                              className='cf3-diario-pass-entry__btn'
                              onClick={() => setDeleteId(null)}
                            >
                              Cancelar
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <p className='cf3-diario-pass-entry__texto'>{entry.texto}</p>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
